import { DEMO_ROLES, type DemoInfo, type DemoRole, type MeResponse } from '@epc/contracts';
import * as DM from '@radix-ui/react-dropdown-menu';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronDown, FlaskConical } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { meQuery, useMe } from '../lib/session';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { ErrorBox, toast } from '../ui/feedback';

/**
 * Новая сессия другим человеком: /me подменяется на месте (подписчики шапки получают обновление), остальные
 * запросы сбрасываются и перечитываются уже с новыми правами и уровнем денег. qc.clear() здесь не годится:
 * он снимает запросы вместе с подписчиками, и часть шапки остаётся со старым именем до случайной перерисовки.
 */
function applyMe(qc: QueryClient, me: MeResponse) {
  qc.setQueryData(meQuery.queryKey, me);
  void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

export const demoInfoQuery = {
  queryKey: ['public', 'demo'],
  queryFn: () => api<DemoInfo>('/public/demo'),
  staleTime: 300_000,
};

/**
 * «Попробовать демо» (ГЛ-03): выбор роли и вход без пароля в демо-компанию на два часа.
 * Кнопка показывается только когда демо включено на этом портале.
 */
export function DemoEntry({
  className,
  label = 'Попробовать демо',
  variant = 'secondary',
  size = 'lg',
  hint = true,
}: {
  className?: string;
  /** Подпись кнопки — главная страница показывает её на выбранном языке */
  label?: string;
  variant?: 'primary' | 'secondary';
  size?: 'md' | 'lg';
  /** Пояснение над кнопкой (на странице входа); на главной его нет */
  hint?: boolean;
}) {
  const info = useQuery(demoInfoQuery);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<DemoRole>('pm');
  const login = useMutation({
    mutationFn: (r: DemoRole) => api<MeResponse>('/public/demo/login', { body: { role: r } }),
    onSuccess: (me) => {
      applyMe(qc, me);
      navigate({ to: '/' });
    },
  });
  if (!info.data?.enabled) return null;
  return (
    <div className={className}>
      {hint ? (
        <p className="mb-3 text-[13px] text-ink-2">
          Хотите посмотреть портал без регистрации? Демо-компания с вымышленными данными во всех разделах —
          вход одной кнопкой, без пароля.
        </p>
      ) : null}
      <Button
        variant={variant}
        size={size}
        icon={<FlaskConical className="size-5" />}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={`Демо-компания ${info.data.companyName}`}
        description={`Вымышленные данные во всех разделах. Сессия на два часа, роль можно сменить в шапке. Каждую ночь в ${info.data.resetAt} данные возвращаются к исходным.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button variant="primary" loading={login.isPending} onClick={() => login.mutate(role)}>
              Войти в демо
            </Button>
          </>
        }
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-medium text-ink">Кем войти</legend>
          {info.data.roles.map((r) => (
            <label
              key={r.code}
              className={clsx(
                'flex min-h-11 cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5',
                role === r.code ? 'border-ink bg-surface-2' : 'border-line hover:border-ink-3',
              )}
            >
              <input
                type="radio"
                name="demo-role"
                value={r.code}
                checked={role === r.code}
                onChange={() => setRole(r.code)}
                className="mt-1 size-4 shrink-0 accent-[var(--color-ink)]"
              />
              <span className="flex min-w-0 flex-col">
                <span className="text-[14px] font-medium text-ink">
                  {r.title} <span className="font-normal text-ink-2">— {r.fullName}</span>
                </span>
                <span className="text-[12px] leading-4 text-ink-2">{r.description}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <ErrorBox className="mt-3" error={login.error} />
      </Dialog>
    </div>
  );
}

/** Плашка демо-режима в шапке: роль и её смена, заявка на доступ, выход */
export function DemoBanner() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = me.user.demoRole;
  const sw = useMutation({
    mutationFn: (r: DemoRole) => api<MeResponse>('/auth/demo/switch', { body: { role: r } }),
    onSuccess: (next) => {
      applyMe(qc, next);
      navigate({ to: '/' });
      toast(
        `Теперь вы — ${DEMO_ROLES.find((r) => r.code === next.user.demoRole)?.title ?? 'демо-пользователь'}`,
      );
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось сменить роль', 'danger'),
  });
  if (!role) return null;
  const current = DEMO_ROLES.find((r) => r.code === role);
  return (
    <div className="border-b border-info/30 bg-info-soft text-ink print:hidden" role="status">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 md:px-8">
        <FlaskConical className="size-5 shrink-0 text-info" aria-hidden />
        <div className="min-w-0 flex-1 basis-[220px] text-[13px] leading-5">
          <span className="font-semibold">Демо-режим.</span>{' '}
          <span className="text-ink-2">Данные вымышленные и каждую ночь возвращаются к исходным.</span>
        </div>
        <DM.Root>
          <DM.Trigger
            className="inline-flex h-11 items-center gap-1 rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink md:h-8"
            disabled={sw.isPending}
          >
            {current?.title ?? role}
            <ChevronDown className="size-4 text-ink-2" aria-hidden />
          </DM.Trigger>
          <DM.Portal>
            <DM.Content
              align="end"
              sideOffset={6}
              className="z-50 w-80 rounded-md border border-line bg-surface p-1.5 text-[14px] shadow-md"
            >
              <div className="px-3 py-1.5 text-[12px] text-ink-2">Посмотреть портал глазами другой роли</div>
              {DEMO_ROLES.map((r) => (
                <DM.Item
                  key={r.code}
                  disabled={r.code === role}
                  onSelect={() => sw.mutate(r.code)}
                  className="flex cursor-pointer flex-col items-start gap-0.5 rounded-sm px-3 py-2 outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-2"
                >
                  <span className="font-medium text-ink">{r.title}</span>
                  <span className="text-[12px] leading-4 text-ink-2">{r.description}</span>
                </DM.Item>
              ))}
            </DM.Content>
          </DM.Portal>
        </DM.Root>
        <Link
          to="/request-access"
          className="inline-flex h-11 items-center px-2 text-[13px] font-medium underline-offset-[3px] hover:underline md:h-8"
        >
          Запросить доступ для своей компании
        </Link>
      </div>
    </div>
  );
}
